import React, {useState} from 'react';
import { Box, Grid2 as Grid, TableContainer, Table, TableHead, TableRow, TableCell, useMediaQuery, useTheme, Paper, Typography, TableSortLabel, TableBody, Stack, styled, Link, FormControl, OutlinedInput, InputAdornment, debounce} from '@mui/material';
import { ExpenditureItem } from '../../../types/reports/expenditureItem';
import { visuallyHidden } from '@mui/utils';
import { Search } from '@mui/icons-material';

interface Props {
   tableRows?:ExpenditureItem[];
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

const ExpendituresScheduleTable = (props: Props): JSX.Element => {

    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));

    const [expenditureRows, setExpenditureRows] = useState<ExpenditureItem[]>(props.tableRows ?? []);
    const [allRows, setAllRows] = useState<ExpenditureItem[]>(props.tableRows ?? []);
    const [orderBy, setOrderBy] = useState('name');
    const [order, setOrder] = useState<'desc' | 'asc'>('asc');

    //const tableColumns = props.ieTableRows?IndependentExpenditureColumns:ExpenditureColumns;
    //const tableRows = props.ieTableRows?props.ieTableRows:props.tableRows;

    let totalCount = props.tableRows?.length ?? 0;
   
    const sortByAmount = () => {
        const sortedRows = [...expenditureRows].sort((a, b) => {
            if (order === 'asc') {
                return b.amountValue - a.amountValue;
            } else {
                return a.amountValue - b.amountValue;
            }
        });
        setExpenditureRows(sortedRows);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const sortByDate = () => {
        const sortedRows = [...expenditureRows].sort((a, b) => {
            if (order === 'asc') {
                return new Date(b.date).getTime() - new Date(a.date).getTime();
            } else {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            }
        });
        setExpenditureRows(sortedRows);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }


    const sortByName = () => {
        const sortedRows = [...expenditureRows].sort((a, b) => {
            if (order === 'asc') {
                return b.fullNameReverse.localeCompare(a.fullNameReverse);
            } else {
                return a.fullNameReverse.localeCompare(b.fullNameReverse);
            }
        });
        setExpenditureRows(sortedRows);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const updateSort = (column: string) => {
        setOrderBy(column);
    }

    const filterByName = debounce((event: any) => {
        let searchInput = event.target.value.toLowerCase();
        if(searchInput.length === 0){
          setExpenditureRows(allRows);
        }
        else{
          let filteredRows = allRows?.filter(
            (row) => (row.fullNameReverse.toLowerCase().includes(searchInput)
            || row.fullAddress.toLowerCase().includes(searchInput)
            || row.description.toLowerCase().includes(searchInput)
            || row.clarifiedVendor?.toLowerCase().includes(searchInput)
            || row.clarifiedPurpose?.toLowerCase().includes(searchInput)
          )
        ) || [];
          setExpenditureRows(filteredRows);
        }
      }, 800);

    /*
    return (
        <Box 
            sx={{ 
            width: '100%',
            '.MuiDataGrid-columnHeaderTitle': { 
                fontWeight: 'bold !important',
            }
        }}>
        <DataGrid 
            rows={tableRows} 
            columns={tableColumns}
            columnVisibilityModel={{
                amount: isSM? true: false,
                amountDisplay: isSM? false: true,
            }}
            getRowHeight={() => 'auto'}
            getRowId={(row) => row.id}
            disableColumnMenu
            hideFooterPagination={totalCount < 100? true: false}
            slots={{ toolbar: QuickSearchToolbar }}
            sx={{ 
                '.MuiDataGrid-cell': {
                  alignItems: 'center',
                  //whiteSpace: 'normal', 
                  wordBreak: 'break-word',
                  paddingTop: '3px!important',
                  paddingBottom: '3px!important',
                },
                '.MuiDataGrid-row--dynamicHeight>.MuiDataGrid-cell':{
                    //whiteSpace: 'normal',
                }
              }}
        />
        </Box>
    );
    */

    return (
        <Grid container spacing={1} marginTop={1} marginBottom={1}>
        <Grid size={12} marginBottom={1}>
            <FormControl size="small" sx={{ maxWidth: 200 }}>
                <OutlinedInput
                    placeholder={"Search ..."}
                    startAdornment={
                        <InputAdornment position="start">
                            <Search />
                            </InputAdornment>
                        }
                    onChange={filterByName}
                    sx={{ fontSize: 'smaller' }}
                />
            </FormControl>
         </Grid>
         <TableContainer component={Paper}>
             <Table aria-label="expenditures table" size="small">
                 <TableHead>
                     <TableRow>
                         <TableCell onClick={sortByDate} onMouseEnter={()=>updateSort('date')}>
                            <Typography
                                variant={'subtitle2'}
                                fontWeight={600}
                                className='is-clickable' 
                            >
                                Date
                            </Typography>
                         </TableCell>
                         <TableCell onClick={sortByName} onMouseEnter={()=>updateSort('name')}>
                            <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={sortByName}
                            >
                                <Typography
                                    variant={'subtitle2'}
                                    fontWeight={600}
                                    className='is-clickable' 
                                 >
                                    Name/Address
                                </Typography>
                                {orderBy === 'name' ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                         </TableCell>
                         {
                            isSM &&
                            <TableCell align='right' onClick={sortByAmount}>
                                <Typography
                                    variant={'subtitle2'}
                                    fontWeight={600}
                                    className='is-clickable' 
                                    >
                                    Amount
                                </Typography>
                            </TableCell>
                         }
                         <TableCell>
                             <Typography
                             variant={'subtitle2'}
                             fontWeight={600}>
                                Type
                             </Typography>
                         </TableCell>
                         <TableCell>
                             <Typography
                             variant={'subtitle2'}
                             fontWeight={600}>
                                Purpose
                             </Typography>
                         </TableCell>
                         {
                            !isSM &&
                            <TableCell onClick={sortByAmount} align='right'>
                                <Typography
                                    variant={'subtitle2'}
                                    fontWeight={600}
                                    className='is-clickable' 
                                    >
                                     Amount
                                </Typography>
                            </TableCell>
                         }
                     </TableRow>
                 </TableHead>
                 <TableBody>
                     {expenditureRows?.map((expenditure) => (
                         <StyledTableRow key={expenditure.id}>
                             <TableCell>
                                 <Typography variant={'body2'}>
                                     {expenditure.date}
                                 </Typography>
                             </TableCell>
                             <TableCell>
                                <>
                                    <Typography variant="subtitle2">
                                        {expenditure.fullNameReverse} {expenditure.vendor}
                                        {
                                            expenditure.linkedReport &&
                                            <Link className="is-clickable" href={`/reports/displayreport?id=${expenditure.linkedReport.reportId}`} target="_blank" underline="hover" color="primary" fontSize="0.75rem">(<i>View Linked Report</i>)</Link>
                                        }
                                    </Typography>
                                    <Typography component="i"  variant="body2" color={"primary"}>{expenditure.clarifiedVendor}</Typography>
                                    <Typography component="i"  variant="body2">{expenditure.fullAddress}</Typography>
                                </>       
                            </TableCell>
                             {
                                isSM &&
                                <TableCell align='right'>
                                    <Typography variant={'body2'}>
                                        {expenditure.amount}
                                    </Typography>
                                </TableCell>
                             }
                             {
                                expenditure.recordTypeId?
                                <TableCell>
                                    <Typography variant={'body2'}>
                                        {expenditure.recordTypeDescription.replace("Expenditure", "")}
                                    </Typography>
                                </TableCell>:
                                <TableCell>
                                    <Typography variant={'body2'}>
                                        {"Expenditure"}
                                    </Typography>
                                </TableCell>
                             }
                             <TableCell>
                                 {
                                    expenditure.recordTypeId === 315 ? //IE PAC Expenditure
                                    <Stack>
                                        <Typography variant="subtitle2">{expenditure.description}</Typography>
                                        <Typography component="i"  variant="body2" color={"primary"}>{expenditure.ieInfo}</Typography>
                                    </Stack>:
                                    <Stack>
                                        <Typography variant="subtitle2">{expenditure.description} {expenditure.purpose}</Typography>
                                        <Typography component="i"  variant="body2" color={"primary"}>{expenditure.clarifiedPurpose}</Typography>
                                    </Stack>   
                                 }
                             </TableCell>
                             {
                                !isSM &&
                                <TableCell align='right'>
                                    <Typography variant={'body2'}>
                                        {expenditure.amount}
                                    </Typography>
                                </TableCell>
                             }   
                         </StyledTableRow>
                    ))}
                 </TableBody>
             </Table>
         </TableContainer>
        </Grid>
    );
}

export default ExpendituresScheduleTable;